import { motion } from 'framer-motion';
import { ArrowDown, Check, Sparkles } from 'lucide-react';
import DashboardMockup from './DashboardMockup';

export const Hero = () => {
  const trustItems = [
    '500+ curated backlink sources',
    'Unlimited projects & campaigns',
    'Add your own custom links',
    'Directory updated continuously',
  ];

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="section-dark relative min-h-screen pt-24 pb-16 overflow-hidden"
    >
      {/* Background gradient effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[128px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[128px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-12">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
            data-testid="hero-badge"
          >
            <Sparkles size={16} className="text-yellow-400" />
            <span className="text-sm text-white/80">500+ Backlink Sources</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
              $9 One-Time
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-6"
            data-testid="hero-headline"
          >
            <span className="text-white">Your Entire Backlink Strategy.</span>
            <br />
            <span className="gradient-text">One Dashboard. $9. Forever.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed"
            data-testid="hero-subheadline"
          >
            Stop juggling spreadsheets. Searchefy gives you a curated directory of 500+ vetted backlink sources, 
            unlimited project campaigns to track every link's status, and real-time analytics — all for a one-time $9 lifetime payment.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6"
          >
            <a
              href="https://checkout.dodopayments.com/buy/pdt_0Nc1Sd7RjzAPh38CNHxE9?quantity=1"
              data-testid="hero-cta-primary"
              className="btn-primary text-base px-8 py-4 flex items-center gap-2 animate-pulse-glow"
            >
              Get Lifetime Access — $9
              <span className="text-xs opacity-80">Pay Once, Use Forever</span>
            </a>
            <a
              href="#how-it-works"
              data-testid="hero-cta-secondary"
              className="btn-secondary text-base px-8 py-4 flex items-center gap-2"
            >
              See How It Works
              <ArrowDown size={18} />
            </a>
          </motion.div>

          {/* Price badge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="inline-flex items-center gap-2 text-sm text-white/60 mb-10"
            data-testid="hero-price-badge"
          >
            <span className="font-semibold text-white">$9 one-time</span>
            <span>·</span>
            <span>No subscription</span>
            <span>·</span>
            <span>No renewal</span>
            <span>·</span>
            <span>Lifetime access</span>
          </motion.div>

          {/* Trust bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap justify-center gap-6"
            data-testid="hero-trust-bar"
          >
            {trustItems.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 text-sm text-white/70"
                data-testid={`trust-item-${index}`}
              >
                <Check size={16} className="text-green-400" />
                <span>{item}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Dashboard Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="relative"
        >
          {/* Arrow indicator */}
          <div className="absolute -top-8 right-8 md:right-24 flex items-center gap-2 text-white/60">
            <span className="text-sm font-medium">424 backlink opportunities</span>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="rotate-90">
              <path d="M10 20C10 20 20 10 30 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M25 15L30 20L25 25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <DashboardMockup />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
